
/**
 * Utility functions to manage subscription redirect states and grace periods
 */

const SUBSCRIPTION_SUCCESS_KEY = 'subscription_success_redirect';
const SUBSCRIPTION_EXPIRED_KEY = 'subscription_expired_grace';
const SUCCESS_GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutes after subscription success
const EXPIRY_GRACE_PERIOD_MS = 2 * 24 * 60 * 60 * 1000; // 2 days after expiration

/**
 * Mark that user is coming from a successful subscription
 * This helps bypass subscription checks temporarily
 * @returns {number} - Expiry time in milliseconds
 */
export const markSuccessfulSubscription = () => {
  const expiryTime = Date.now() + SUCCESS_GRACE_PERIOD_MS;
  localStorage.setItem(SUBSCRIPTION_SUCCESS_KEY, expiryTime.toString());
  
  // Also set session storage to handle page refreshes during the current session
  sessionStorage.setItem(SUBSCRIPTION_SUCCESS_KEY, expiryTime.toString());
  
  console.log('SubscriptionRedirectHelper: Marked successful subscription, grace period active until', 
    new Date(expiryTime).toLocaleTimeString(), '(5 minutes)');
  
  return expiryTime;
};

/**
 * Mark that a user's subscription has expired
 * This sets up a grace period during which they still have access
 * @param {string} planType - The type of plan that expired ('trial', 'free', or 'paid')
 * @returns {number} - Expiry time of the grace period in milliseconds
 */
export const markPlanExpired = (planType: string) => {
  const expiryTime = Date.now() + EXPIRY_GRACE_PERIOD_MS;
  const storageKey = `${SUBSCRIPTION_EXPIRED_KEY}_${planType}`;
  
  localStorage.setItem(storageKey, expiryTime.toString());
  sessionStorage.setItem(storageKey, expiryTime.toString());
  
  console.log(`SubscriptionRedirectHelper: Marked ${planType} plan expired, grace period active until`, 
    new Date(expiryTime).toLocaleTimeString(), '(2 days)');
  
  return expiryTime;
};

/**
 * Check if user is in the subscription grace period
 * @returns {boolean} - true if user should bypass subscription checks temporarily
 */
export const isInSubscriptionGracePeriod = (): boolean => {
  // Check both localStorage and sessionStorage for the grace period
  const expiryTimeStr = localStorage.getItem(SUBSCRIPTION_SUCCESS_KEY) || 
                        sessionStorage.getItem(SUBSCRIPTION_SUCCESS_KEY);
  
  if (!expiryTimeStr) {
    return false;
  }
  
  const expiryTime = parseInt(expiryTimeStr);
  const now = Date.now();
  
  // If grace period has expired, clean up the items
  if (now > expiryTime) {
    clearSubscriptionGracePeriod();
    return false;
  }
  
  // Calculate remaining time in seconds and minutes
  const remainingSeconds = Math.round((expiryTime - now) / 1000);
  const remainingMinutes = Math.round(remainingSeconds / 60);
  console.log(`SubscriptionRedirectHelper: In grace period, ${remainingMinutes} minutes (${remainingSeconds} seconds) remaining`);
  
  return true;
};

/**
 * Check if user is in the post-expiry grace period for a specific plan type
 * @param {string} planType - The type of plan to check ('trial', 'free', or 'paid')
 * @returns {boolean} - true if user is in the grace period after expiration
 */
export const isInPostExpiryGracePeriod = (planType: string): boolean => {
  const storageKey = `${SUBSCRIPTION_EXPIRED_KEY}_${planType}`;
  
  // Check both localStorage and sessionStorage for the grace period
  const expiryTimeStr = localStorage.getItem(storageKey) || 
                        sessionStorage.getItem(storageKey);
  
  if (!expiryTimeStr) {
    return false;
  }
  
  const expiryTime = parseInt(expiryTimeStr);
  const now = Date.now();
  
  // If grace period has expired, clean up the items
  if (now > expiryTime) {
    clearPlanExpiredGracePeriod(planType);
    return false;
  }
  
  // Calculate remaining time in hours
  const remainingHours = Math.round((expiryTime - now) / (1000 * 60 * 60));
  console.log(`SubscriptionRedirectHelper: In post-expiry grace period for ${planType}, ${remainingHours} hours remaining`);
  
  return true;
};

/**
 * Get remaining time in the grace period
 * @returns {number} - Seconds remaining in grace period, 0 if not in grace period
 */
export const getGracePeriodRemainingSeconds = (): number => {
  // Check both localStorage and sessionStorage for the grace period
  const expiryTimeStr = localStorage.getItem(SUBSCRIPTION_SUCCESS_KEY) || 
                        sessionStorage.getItem(SUBSCRIPTION_SUCCESS_KEY);
  
  if (!expiryTimeStr) {
    return 0;
  }
  
  const expiryTime = parseInt(expiryTimeStr);
  const now = Date.now();
  
  // If grace period has expired, clean up the items and return 0
  if (now > expiryTime) {
    clearSubscriptionGracePeriod();
    return 0;
  }
  
  return Math.round((expiryTime - now) / 1000);
};

/**
 * Get remaining time in hours for the post-expiry grace period
 * @param {string} planType - The type of plan to check ('trial', 'free', or 'paid')
 * @returns {number} - Hours remaining in post-expiry grace period, 0 if not in grace period
 */
export const getPostExpiryGraceHoursRemaining = (planType: string): number => {
  const storageKey = `${SUBSCRIPTION_EXPIRED_KEY}_${planType}`;
  
  // Check both localStorage and sessionStorage for the grace period
  const expiryTimeStr = localStorage.getItem(storageKey) || 
                        sessionStorage.getItem(storageKey);
  
  if (!expiryTimeStr) {
    return 0;
  }
  
  const expiryTime = parseInt(expiryTimeStr);
  const now = Date.now();
  
  // If grace period has expired, clean up the items and return 0
  if (now > expiryTime) {
    clearPlanExpiredGracePeriod(planType);
    return 0;
  }
  
  return Math.round((expiryTime - now) / (1000 * 60 * 60));
};

/**
 * Clear the subscription grace period marker
 */
export const clearSubscriptionGracePeriod = () => {
  localStorage.removeItem(SUBSCRIPTION_SUCCESS_KEY);
  sessionStorage.removeItem(SUBSCRIPTION_SUCCESS_KEY);
  console.log('SubscriptionRedirectHelper: Grace period cleared');
};

/**
 * Clear the post-expiry grace period marker for a specific plan type
 * @param {string} planType - The type of plan to clear ('trial', 'free', or 'paid')
 */
export const clearPlanExpiredGracePeriod = (planType: string) => {
  const storageKey = `${SUBSCRIPTION_EXPIRED_KEY}_${planType}`;
  localStorage.removeItem(storageKey);
  sessionStorage.removeItem(storageKey);
  console.log(`SubscriptionRedirectHelper: Post-expiry grace period for ${planType} cleared`);
};
